import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import WebApp from '@twa-dev/sdk';
import { FaGift, FaTelegramPlane } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import useUserStore from '../stores/userStore';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.background};
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
`;

const GiftIcon = styled(FaGift)`
  color: ${props => props.theme.text.primary};
  opacity: 0.8;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 20px;
`;

const Description = styled.p`
  font-size: 14px;
  color: ${props => props.theme.text.secondary};
  margin: 0;
  line-height: 1.5;
  text-align: center;
`;

const InputContainer = styled.div`
  margin-bottom: 16px;
`;

const CodeInput = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid ${props => props.hasError ? '#ff4444' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  padding: 12px 16px;
  color: ${props => props.theme.text.primary};
  font-size: 14px;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ff4444' : props.theme.primary};
    background: rgba(255, 255, 255, 0.07);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #ff4444;
  font-size: 12px;
  margin-top: 8px;
  padding: 8px 12px;
  background: rgba(255, 68, 68, 0.1);
  border-radius: 8px;
  border-left: 3px solid #ff4444;
`;

const SaveButton = styled.button`
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 12px;
  padding: 14px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const InfoSection = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
`;

const InfoTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.text.primary};
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoText = styled.p`
  font-size: 14px;
  color: ${props => props.theme.text.secondary};
  margin: 0;
  line-height: 1.6;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: #0095ff;
  font-size: 14px;
  cursor: pointer;
  padding: 8px 0;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  &:hover { opacity: 0.8; }
`;

export default function ClaimFragsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fetchUser = useUserStore(state => state.fetchUser);

  const [code, setCode] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (WebApp.platform !== 'unknown') {
      WebApp.BackButton.show();
      WebApp.BackButton.onClick(() => navigate(-1));
    }
    return () => {
      if (WebApp.platform !== 'unknown') {
        WebApp.BackButton.hide();
        WebApp.BackButton.offClick();
      }
    };
  }, [navigate]);

  const handleChange = (e) => {
    setCode(e.target.value.toUpperCase());
    if (error) setError('');
  };

  const handleRedeem = async () => {
    if (!code.trim()) {
      setError(t('common.error'));
      return;
    }
    setIsSaving(true);
    setError('');
    try {
      await apiService.claimFragsByCode(code.trim());
      toast.success(t('common.success'), { position: 'top-center', theme: 'dark' });
      setCode('');
      await fetchUser();
    } catch (e) {
      const backendError = e?.response?.data?.error;
      if (backendError) {
        setError(backendError);
      } else {
        setError(t('common.error'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChannel = () => {
    const url = 'https://t.me/skinforge';
    if (WebApp.platform !== 'unknown') {
      WebApp.openLink(url);
    } else {
      window.open(url, '_blank');
    }
  };

  return (
    <Container>
      <Content>
        <LogoContainer>
          <GiftIcon size={80} />
        </LogoContainer>

        <FormSection>
          <Description>
            {t('promo.usePromo')}
          </Description>

          <InputContainer>
            <CodeInput
              value={code}
              onChange={handleChange}
              placeholder="CODE123"
              hasError={!!error}
            />
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </InputContainer>

          <SaveButton onClick={handleRedeem} disabled={isSaving || !code.trim()}>
            {isSaving ? t('tradeUrl.saving') : t('promo.claimFrags')}
          </SaveButton>

          <InfoSection>
            <InfoText>
              {t('promo.channelInfo')}
            </InfoText>
            <LinkButton onClick={handleOpenChannel}>
              <FaTelegramPlane size={16} /> {t('promo.subscribeChannel')}
            </LinkButton>
          </InfoSection>
        </FormSection>
      </Content>
    </Container>
  );
}


